// /teams/:teamId/billing — usage panel per PRD §"Cost meters". Renders the
// current-month rollup + a sparkline-style per-day breakdown for the last
// 30 days. BYOK-aware: the LLM-tokens line shows the OpenRouter dashboard
// link when the team has its own key wired (currently always Cairn-managed
// until #16 lands the BYOK secret-storage pipe).

import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { usagePeriodId, useTeam, useUsageDays, useUsageMonth } from '../../lib/data-hooks.js'

const DAILY_CAPS = {
  recordings: 50,
  runs: 200,
  scans: 100,
}

const PCT_WARN = 0.8

function MetricCard({ label, value, unit, cap, hint }) {
  let cls = 'border-[var(--color-sandstone)]'
  if (cap && value >= cap) {
    cls = 'border-[var(--color-failed)] bg-[var(--color-failed)]/10'
  } else if (cap && value / cap >= PCT_WARN) {
    cls = 'border-[var(--color-running)] bg-[var(--color-running)]/10'
  }
  return (
    <div className={`rounded-md border bg-[var(--color-limestone)] px-5 py-4 ${cls}`}>
      <div className="mb-1 text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
        {label}
      </div>
      <div className="font-mono text-2xl text-[var(--color-basalt)]">
        {value}
        {unit && <span className="ml-1 text-xs text-[var(--color-schist)]">{unit}</span>}
        {cap && <span className="ml-2 text-xs text-[var(--color-schist)]">/ {cap}</span>}
      </div>
      {hint && <div className="mt-2 text-xs text-[var(--color-schist)]">{hint}</div>}
    </div>
  )
}

function DailyHistogram({ days, kind }) {
  if (!days?.length) return null
  const values = days.map((d) => d[kind] ?? 0)
  const max = Math.max(...values, 1)
  return (
    <div className="flex h-12 items-end gap-0.5">
      {days
        .slice(0, 30)
        .reverse()
        .map((d) => {
          const v = d[kind] ?? 0
          const pct = (v / max) * 100
          return (
            <span
              key={d.id}
              title={`${d.id}: ${v}`}
              className="block w-2 rounded-sm bg-[var(--color-terracotta-soft)]"
              style={{ height: `${Math.max(pct, 2)}%` }}
            />
          )
        })}
    </div>
  )
}

export function Billing() {
  const { teamId } = useParams()
  const { data: team } = useTeam(teamId)
  const periodId = useMemo(() => usagePeriodId(), [])
  const { data: monthly, loading, error } = useUsageMonth(teamId, periodId)
  const { data: days } = useUsageDays(teamId, periodId)

  const usingBYOK = Boolean(team?.openrouter?.apiKeyRef)
  const todayId = days[0]?.id
  const today = days[0] ?? {}

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: 'Teams', to: '/teams' },
          { label: team?.name ?? teamId, to: `/teams/${teamId}` },
          { label: 'Billing' },
        ]}
        title="Usage"
        subhead={`Current month (${periodId}). Daily caps reset at UTC midnight.`}
      />

      <div className="mx-auto max-w-5xl px-6 py-8">
        {loading && <p className="text-sm text-[var(--color-schist)]">Loading usage…</p>}
        {error && (
          <p className="text-sm text-[var(--color-failed)]" role="alert">
            {error.message}
          </p>
        )}

        <section className="mb-8">
          <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
            Today {todayId ? `(${todayId})` : ''}
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <MetricCard
              label="Recordings"
              value={today.recordings ?? 0}
              cap={DAILY_CAPS.recordings}
            />
            <MetricCard label="Test runs" value={today.runs ?? 0} cap={DAILY_CAPS.runs} />
            <MetricCard label="Scans" value={today.scans ?? 0} cap={DAILY_CAPS.scans} />
          </div>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
            Month to date
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              label="Voice minutes"
              value={(monthly?.voiceMinutes ?? 0).toFixed(1)}
              unit="min"
              hint="Transcription via AssemblyAI; always Cairn-managed."
            />
            {usingBYOK ? (
              <MetricCard
                label="LLM tokens"
                value="BYOK"
                hint={
                  <>
                    See your{' '}
                    <a
                      href="https://openrouter.ai/activity"
                      className="text-[var(--color-terracotta)] hover:underline"
                    >
                      OpenRouter activity dashboard
                    </a>{' '}
                    for token + cost detail.
                  </>
                }
              />
            ) : (
              <MetricCard
                label="LLM tokens (in / out)"
                value={`${monthly?.llmTokensIn ?? 0} / ${monthly?.llmTokensOut ?? 0}`}
                hint="Routed through Cairn's OpenRouter key."
              />
            )}
            <MetricCard
              label="Wallclock"
              value={Math.round((monthly?.wallclockSeconds ?? 0) / 60)}
              unit="min"
            />
            <MetricCard label="Recordings" value={monthly?.recordings ?? 0} />
            <MetricCard label="Test runs" value={monthly?.runs ?? 0} />
            <MetricCard label="Scans" value={monthly?.scans ?? 0} />
          </div>
        </section>

        {days.length > 0 && (
          <section>
            <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
              Last 30 days
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-4">
                <div className="mb-2 text-xs text-[var(--color-schist)]">Recordings / day</div>
                <DailyHistogram days={days} kind="recordings" />
              </div>
              <div className="rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-4">
                <div className="mb-2 text-xs text-[var(--color-schist)]">Runs / day</div>
                <DailyHistogram days={days} kind="runs" />
              </div>
              <div className="rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-4">
                <div className="mb-2 text-xs text-[var(--color-schist)]">Scans / day</div>
                <DailyHistogram days={days} kind="scans" />
              </div>
            </div>
          </section>
        )}

        <p className="mt-10 text-xs text-[var(--color-schist)]">
          Pricing tiers are not enabled yet — this panel is here so you can see the system
          accounting from day one. Caps are enforced server-side; you'll see a clear error if a job
          is rejected for the day.
        </p>
      </div>
    </>
  )
}
